import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TranslateDirective } from '@ngx-translate/core';
import { IEnumFormControlConfig } from '@app/core/interfaces/form-control-config';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { getFormOptions, IFormOptions } from '@app/shared/utils/form-utils';
import { SelectModule } from 'primeng/select';
import { MessageModule } from 'primeng/message';
import { mapObjectBy } from '@app/shared/utils/array-mappers';
import { SvgIconComponent } from 'angular-svg-icon';

@Component({
  selector: 'gc-floating-popup-select-date-range-action',
  standalone: true,
  imports: [
    ButtonModule,
    TranslateDirective,
    SelectModule,
    MessageModule,
    ButtonModule,
    ReactiveFormsModule,
    SvgIconComponent,
  ],
  templateUrl: './floating-popup-select-date-range-action.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FloatingPopupSelectDateRangeActionComponent<T extends string = string> implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  public facade = input.required<IEnumFormControlConfig<T>>();

  private facade$ = toObservable(this.facade);

  public formGroup?: FormGroup<{
    data: FormControl<T | null>;
  }>;

  public formOptions?: T[];
  public dataOptions?: IFormOptions<T>[];
  public dataOptionsMap?: Record<T, IFormOptions<T>>;

  ngOnInit(): void {
    this.facade$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.initComponent();
    });
  }

  public selectLabel(value: T): IFormOptions<T> | null {
    if (!this.dataOptionsMap) return null;

    return this.dataOptionsMap[value];
  }

  private initComponent() {
    const facade = this.facade();

    this.formGroup = new FormGroup({
      data: new FormControl<T | null>(null, [Validators.required]),
    });

    this.dataOptions = getFormOptions<T>(facade.values, facade.localize);
    this.formOptions = this.dataOptions.map(el => el.value);
    this.dataOptionsMap = mapObjectBy<IFormOptions<T>, T>(this.dataOptions, el => el.value);

    this.initSubscriptions();
  }

  private initSubscriptions() {
    const facade = this.facade();

    facade
      .getValue()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.formGroup?.controls['data'].setValue(value || facade.defaultValue, {
          emitEvent: false,
        });
      });

    this.formGroup?.controls['data'].valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      facade.valueChange(value || facade.defaultValue);
      queueMicrotask(() => this.cdr.detectChanges());
    });
  }
}
